import { type CommandHandler } from '@telespot/core';

import { UpdatePipelineAlgorithm } from './update-pipeline-algorithm.command';
import { type AlgorithmRepository, type PipelineRepository, PipelineStep } from '@telespot/domain';

/**
 * @group Protocols
 * @category Errors
 */
export class UpdatePipelineAlgorithmError extends Error {
  constructor(public readonly entityType: 'algorithm' | 'pipelineStep' | 'pipeline', public readonly entityId: string) {
    const message = `${entityType} not found with id ${entityId}`;
    super(message);
  }
}

export type IUpdatePipelineAlgorithmHandler = CommandHandler<UpdatePipelineAlgorithm>;

/**
 * Updates an existing pipeline step to use a different algorithm.
 * @group Protocols
 * @category Handlers
 */
export class UpdatePipelineAlgorithmHandler implements IUpdatePipelineAlgorithmHandler {
  constructor(
    private readonly pipelineRepository: PipelineRepository,
    private readonly algorithmRepository: AlgorithmRepository
  ) {}

  public async execute(command: UpdatePipelineAlgorithm): Promise<void> {
    const { pipelineStepId, algorithmId } = command.details;

    // Check if the algorithm exists
    if (algorithmId !== undefined) {
      try {
        await this.algorithmRepository.getById(algorithmId);
      } catch (error) {
        throw new UpdatePipelineAlgorithmError('algorithm', algorithmId);
      }
    }

    // Check if the pipeline step exists
    let pipelineStep: PipelineStep;
    try {
      pipelineStep = await this.pipelineRepository.getStepById(pipelineStepId);
    } catch (error) {
      throw new UpdatePipelineAlgorithmError('pipelineStep', pipelineStepId);
    }

    // Check if the pipeline exists
    try {
      await this.pipelineRepository.getById(pipelineStep.pipelineId);
    } catch (error) {
      throw new UpdatePipelineAlgorithmError('pipeline', pipelineStep.pipelineId);
    }

    // Finally, update the pipeline step
    pipelineStep.changeExecutor(algorithmId);
    await this.pipelineRepository.saveStep(pipelineStep);
  }
}
